import {
  E_INVOICE_MAX_XML_BYTES, EInvoiceReadError, eInvoiceReadErrorText, eInvoiceSummary, readEInvoiceFile, sniffEInvoiceKind,
  type EInvoiceFile, type EInvoiceSummary,
} from "@talentkontor/domain";
import { sha256Hex } from "./invoice-document";

/**
 * Steuerprüfung L12: E-Rechnungen beim Hochladen von Creator-Rechnungen und Belegen (Creator-Portal und Agentur). Ein Weg für
 * alle Upload-Stellen: Datei prüfen (Inhaltstyp, Größe, Inhalt), XML bzw. das in ZUGFeRD/Factur-X eingebettete XML lesen
 * (packages/domain/src/e-invoice-read.ts: ohne DTD, Größengrenzen) und die Kurzfassung mit Prüfsumme für die Anlage bilden.
 * Die Datei selbst geht unverändert in den Speicher; sie ist das Original.
 */

/**
 * Fehler in der hochgeladenen Datei (falscher Typ, zu groß, DTD bzw. Entitäten, beschädigt): ein Eingabefehler, kein Fehler bei
 * uns. Die Aktionen zeigen ihn als bleibenden Hinweis ohne „Melden“ (Abnahme E-4).
 */
export class EingabeFehler extends Error {
  readonly melden = false;
  constructor(text: string) {
    super(text);
    this.name = "EingabeFehler";
  }
}

/** accept-Attribut der Dateiauswahl: wie bisher PDF und Bilder, dazu XML (XRechnung) */
export const BELEG_ACCEPT = "application/pdf,image/jpeg,image/png,.xml,application/xml,text/xml";
/** Inhaltstypen, unter denen Browser eine XML-Datei melden (ohne Angabe: Endung entscheidet) */
const XML_TYPES = ["application/xml", "text/xml", ""];

export function isXmlUpload(file: { type: string; name?: string | null }): boolean {
  if (file.type === "application/xml" || file.type === "text/xml") return true;
  return file.type === "" && /\.xml$/i.test(file.name ?? "");
}

/** Pfad zeigt auf ein XML (E-Rechnung als Rechnungsdatei) */
export function isXmlPath(path: string | null | undefined): boolean {
  return /\.xml$/i.test(path ?? "");
}

/** Größenprüfung für XML (5 MB); null = in Ordnung */
export function xmlUploadProblem(file: { size: number; type: string; name?: string | null }, lang: "de" | "en" = "de"): string | null {
  if (!XML_TYPES.includes(file.type) || !isXmlUpload({ type: file.type, name: file.name ?? "x.xml" })) return eInvoiceReadErrorText("not_einvoice", lang);
  if (file.size <= 0) return eInvoiceReadErrorText("empty", lang);
  if (file.size > E_INVOICE_MAX_XML_BYTES) return eInvoiceReadErrorText("too_large", lang);
  return null;
}

export interface PreparedUpload {
  /** hochzuladende Datei: XML mit festem Inhaltstyp application/xml, sonst unverändert */
  blob: Blob;
  mime: string;
  sha256: string;
  eInvoice: EInvoiceSummary | null;
  /** Hinweis ohne Sperre (z. B. eingebettete E-Rechnung im PDF nicht lesbar) */
  warning: string | null;
}

/**
 * Datei für den Upload vorbereiten. XML muss eine lesbare E-Rechnung sein (sonst Fehler mit Klartext); ein PDF wird auf eine
 * eingebettete E-Rechnung geprüft und bleibt sonst ein gewöhnliches PDF; Bilder unverändert. Die Grundprüfung (Art, 15 MB)
 * macht die aufrufende Stelle vorher.
 */
export async function prepareUpload(file: Blob, fileName: string, lang: "de" | "en" = "de", origin: "uploaded" | "generated" = "uploaded"): Promise<PreparedUpload> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const sha256 = await sha256Hex(bytes);
  if (isXmlUpload({ type: file.type, name: fileName })) {
    const problem = xmlUploadProblem({ size: file.size, type: file.type, name: fileName }, lang);
    if (problem) throw new EingabeFehler(problem);
    if (sniffEInvoiceKind(bytes) !== "xml") throw new EingabeFehler(eInvoiceReadErrorText("not_einvoice", lang));
    const f = await readOrThrow(bytes, fileName, lang);
    return { blob: new Blob([bytes as BlobPart], { type: "application/xml" }), mime: "application/xml", sha256, eInvoice: eInvoiceSummary(f, sha256, origin), warning: null };
  }
  if (file.type === "application/pdf") {
    try {
      const f = await readEInvoiceFile(bytes, { fileName });
      return { blob: file, mime: file.type, sha256, eInvoice: eInvoiceSummary(f, await sha256Hex(f.xml), origin), warning: null };
    } catch (e) {
      const code = e instanceof EInvoiceReadError ? e.code : "pdf_unreadable";
      // kein ZUGFeRD: gewöhnliches PDF; sonst Hinweis, das PDF bleibt trotzdem der Beleg
      const warning = code === "no_xml_in_pdf" || code === "pdf_unreadable" || code === "not_einvoice" ? null
        : lang === "en" ? `The e-invoice embedded in the PDF cannot be read (${eInvoiceReadErrorText(code, lang)}) The PDF is stored as an ordinary PDF.`
        : `Die im PDF eingebettete E-Rechnung ist nicht lesbar (${eInvoiceReadErrorText(code, lang)}) Das PDF wird als gewöhnliches PDF abgelegt.`;
      return { blob: file, mime: file.type, sha256, eInvoice: null, warning };
    }
  }
  return { blob: file, mime: file.type, sha256, eInvoice: null, warning: null };
}

async function readOrThrow(bytes: Uint8Array, fileName: string, lang: "de" | "en"): Promise<EInvoiceFile> {
  try {
    return await readEInvoiceFile(bytes, { fileName });
  } catch (e) {
    if (e instanceof EInvoiceReadError) throw new EingabeFehler(eInvoiceReadErrorText(e.code, lang));
    throw e;
  }
}

/** Felder der Anlage für attachments (Prüfsumme immer, Kurzfassung bei E-Rechnungen) */
export function attachmentEInvoiceFields(p: PreparedUpload): { sha256: string; e_invoice?: EInvoiceSummary } {
  return p.eInvoice ? { sha256: p.sha256, e_invoice: p.eInvoice } : { sha256: p.sha256 };
}

export interface EInvoiceView {
  file: EInvoiceFile;
  /** Prüfsumme der geladenen Datei */
  sha256: string;
  /** stimmt sie mit der beim Hochladen gespeicherten überein? null = keine gespeichert */
  unchanged: boolean | null;
}

/** Original aus dem Speicher lesen und prüfen (Sichtansicht): Prüfsumme gegen die gespeicherte, Inhalt neu gelesen */
export async function readStoredEInvoice(blob: Blob, storedSha256?: string | null, lang: "de" | "en" = "de", fileName?: string | null): Promise<EInvoiceView> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  const sha256 = await sha256Hex(bytes);
  const file = await readOrThrow(bytes, fileName ?? "", lang);
  return { file, sha256, unchanged: storedSha256 ? storedSha256 === sha256 : null };
}

/** Dateiname im ZIP bzw. beim Herunterladen: Endung der gespeicherten Datei (.xml bleibt .xml) */
export function withStoredExtension(name: string, storagePath: string | null | undefined): string {
  if (!isXmlPath(storagePath)) return name;
  return /\.pdf$/i.test(name) ? name.replace(/\.pdf$/i, ".xml") : /\.xml$/i.test(name) ? name : `${name}.xml`;
}

// ── E-1 zu L12 (0448): Prüfsumme und Kurzfassung vom Server ────────────────────────────────────────────────────────

/** Spalten der Anlage aus der serverseitigen Prüfung (Serverfunktion e-rechnung-pruefen, nur sie schreibt sie) */
export interface ServerPruefFelder {
  sha256?: string | null;
  e_invoice?: EInvoiceSummary | null;
  sha256_server?: string | null;
  e_invoice_server?: EInvoiceSummary | null;
  e_invoice_check?: "ok" | "mismatch" | "failed" | null;
  e_invoice_checked_at?: string | null;
  e_invoice_check_error?: string | null;
  e_invoice_mismatch?: string[] | null;
}

/** verified = geprüft und gleich, mismatch = Browser wich ab (Server gilt), failed = fehlgeschlagen, pending = noch nicht geprüft */
export type EInvoicePruefung = "verified" | "mismatch" | "failed" | "pending";

/**
 * Anlage mit den maßgeblichen Werten: nach der Prüfung die des Servers (sha256, e_invoice), sonst wie bisher die des Browsers.
 * Die Angabe des Browsers bleibt in e_invoice_browser; pruefung sagt, woher die Werte stammen (null: keine E-Rechnung, ungeprüft).
 */
export function mitServerPruefung<A extends ServerPruefFelder>(a: A): A & { e_invoice_browser: EInvoiceSummary | null; pruefung: EInvoicePruefung | null } {
  const browser = a.e_invoice ?? null;
  if (a.e_invoice_check === "ok" || a.e_invoice_check === "mismatch") {
    return { ...a, sha256: a.sha256_server ?? a.sha256 ?? null, e_invoice: a.e_invoice_server ?? null, e_invoice_browser: browser,
      pruefung: a.e_invoice_check === "ok" ? "verified" : "mismatch" };
  }
  if (a.e_invoice_check === "failed") return { ...a, sha256: a.sha256_server ?? a.sha256 ?? null, e_invoice_browser: browser, pruefung: "failed" };
  return { ...a, e_invoice_browser: browser, pruefung: browser ? "pending" : null };
}

/**
 * Anhänge, deren E-Rechnung nur der Browser gemeldet hat (noch keine Serverprüfung): die Rechnungsansicht der Agentur holt die
 * Prüfung nach, damit ein Client, der den Aufruf auslässt, nicht dauerhaft bei den eigenen Angaben bleibt (Abnahme E-1).
 */
export function offeneServerPruefungen(attachments: readonly { id: string; pruefung?: EInvoicePruefung | null }[]): string[] {
  return attachments.filter((a) => a.pruefung === "pending").map((a) => a.id);
}

const PRUEF_FEHLER: Record<string, [string, string]> = {
  file_missing: ["Datei im Speicher nicht gefunden", "file not found in storage"],
  server_error: ["Fehler bei der Prüfung", "error during the check"],
};

/** Hinweis zur Herkunft der Werte (Rechnungsansicht, Creator-Portal); null = nichts zu sagen */
export function eInvoicePruefHinweis(a: { pruefung?: EInvoicePruefung | null; e_invoice_check_error?: string | null } | null | undefined, lang: "de" | "en" = "de"): string | null {
  const t = (de: string, en: string) => (lang === "en" ? en : de);
  switch (a?.pruefung) {
    case "pending": return t("Noch nicht serverseitig geprüft: Prüfsumme und Werte stammen aus dem Browser.", "Not yet verified on the server: checksum and values come from the browser.");
    case "failed": {
      const code = a.e_invoice_check_error ?? "server_error";
      const grund = PRUEF_FEHLER[code]?.[lang === "en" ? 1 : 0] ?? eInvoiceReadErrorText(code as Parameters<typeof eInvoiceReadErrorText>[0], lang).replace(/\.$/, "");
      return t(`Serverseitige Prüfung fehlgeschlagen (${grund}). Die Werte stammen aus dem Browser.`, `Server-side check failed (${grund}). The values come from the browser.`);
    }
    case "mismatch": return t("Die Angaben des Browsers wichen ab; es gelten die serverseitig geprüften Werte.", "The browser's details differed; the server-verified values apply.");
    default: return null;
  }
}

/**
 * Nach dem Upload: Serverfunktion e-rechnung-pruefen bildet Prüfsumme und Kurzfassung selbst. Scheitert der Aufruf (Netz, Demo),
 * bleibt der Upload, die Anlage gilt als „noch nicht serverseitig geprüft“. Nur PDF und XML; Bilder enthalten keine E-Rechnung.
 */
export async function serverPruefen(api: { eRechnungPruefen?: (body: { attachment?: string; path?: string }) => Promise<unknown> },
  ref: { attachment?: string | null; path?: string | null }, p: Pick<PreparedUpload, "mime" | "eInvoice">): Promise<boolean> {
  if (!api.eRechnungPruefen || (!p.eInvoice && p.mime !== "application/pdf" && p.mime !== "application/xml")) return false;
  const body = ref.attachment ? { attachment: ref.attachment } : ref.path ? { path: ref.path } : null;
  if (!body) return false;
  try {
    await api.eRechnungPruefen(body);
    return true;
  } catch {
    return false;
  }
}
